/**
 * History's settled-bets list (docs/history-ux.md §1): filter pills (result on the server; venue
 * and league on the client, with an honest "filtering loaded bets" note), the selected calendar
 * day as a removable chip with that day's stats above the list, an outcome/team search, "Load
 * more" paging, and rows that expand to the order's resolution detail.
 *
 * Revision (docs/history-ux.md): a Bets / Plays toggle. Plays folds settled legs that share an
 * opportunity (2+ of them: an arb, a market making quote, a hedged bet) into one row with the net
 * P&L; tap it for the legs. Grouping only sees loaded pages, and says so.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Contest, Order } from "@greenbook/client";
import { ErrorState, Skeleton } from "../ui.js";
import { VenueLogo } from "../VenueLogo.js";
import { ResolutionDetail, type ResolutionState } from "../ResolutionDetail.js";
import { fmtRoi } from "../../lib/format.js";
import { teamsOf, teamLabel, matchupSep } from "../../lib/marketsBoard.js";
import { shortOutcome } from "../../lib/portfolio.js";
import { outcomeLabel, PLAY_LABEL, type SettledItem, type SettledPlay } from "../../lib/plays.js";
import {
  betProfit,
  fmtAmt,
  fmtSignedAmt,
  RESULT_FILTERS,
  type Book,
  type GroupLike,
  type ResultFilter,
} from "../../lib/history.js";

/** "Brewers @ Cubs" (or the contest title for futures). */
export function gameLabel(c: Contest | undefined): string | null {
  if (!c) return null;
  const t = teamsOf(c);
  return t ? `${teamLabel(t.away)} ${matchupSep(c)} ${teamLabel(t.home)}` : (c.title ?? null);
}

/** Everything a search for a team should hit: title, short and full team names. */
export function gameSearchText(c: Contest | undefined): string {
  if (!c) return "";
  const t = teamsOf(c);
  const names = t
    ? [t.home, t.away].flatMap((x) => {
        const r = x as { name?: string; abbr?: string; full_label?: string; market_name?: string };
        return [r.name, r.abbr, r.full_label, r.market_name];
      })
    : [];
  return [c.title, ...names].filter(Boolean).join(" ");
}

export interface DaySummary {
  day: string;
  group: GroupLike | null;
  loading: boolean;
}

export interface SettledListProps {
  ccy: Book;
  rows: Order[];
  loading: boolean;
  error: unknown;
  isError: boolean;
  onRetry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Rows loaded from the server (before the client-side filters). */
  loadedCount: number;
  clientFiltering: boolean;
  /** Filters */
  result: ResultFilter | null;
  onResult: (r: ResultFilter | null) => void;
  venue: string | null;
  onVenue: (v: string | null) => void;
  venues: { key: string; label: string }[];
  league: string | null;
  onLeague: (v: string | null) => void;
  leagues: { key: string; label: string }[];
  q: string;
  onQ: (q: string) => void;
  day: DaySummary | null;
  onClearDay: () => void;
  onClearAll: () => void;
  /** Lookups */
  contestOf: (id: string) => Contest | undefined;
  leagueName: (contestId: string | null | undefined) => string | null;
  logos: Map<string, string>;
  useResolution: (id: string, enabled: boolean) => ResolutionState;
  /** Shown when nothing at all matches. */
  emptyText: string;
  /** Bets (one row per order) or Plays (an opportunity's legs folded into one row). */
  view: ListView;
  onView: (v: ListView) => void;
  /** The Plays view's rows (`rows` grouped by opportunity). */
  items: SettledItem<Order>[];
}

export type ListView = "bets" | "plays";

export function SettledList(p: SettledListProps) {
  const anyFilter = !!p.result || !!p.venue || !!p.league || !!p.q.trim() || !!p.day;
  const plays = p.view === "plays";
  const shown = plays ? p.items.length : p.rows.length;
  const playCount = plays ? p.items.filter((i) => i.type === "play").length : 0;
  return (
    <section aria-label="Settled bets" className="rounded-lg border border-border bg-panel-2">
      <div className="flex flex-col gap-2 p-3 lg:p-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-dim">Settled {plays ? "plays & bets" : "bets"}</h2>
          {!p.loading && (
            <span className="nums text-[11px] text-faint">
              {shown}
              {p.hasMore ? "+" : ""}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {anyFilter && (
              <button type="button" onClick={p.onClearAll} className="min-h-11 text-[12px] text-dim hover:text-amber lg:min-h-0">
                Clear filters
              </button>
            )}
            <div className="flex items-center gap-1" role="group" aria-label="List">
              <Chip active={!plays} onClick={() => p.onView("bets")}>
                Bets
              </Chip>
              <Chip active={plays} onClick={() => p.onView("plays")}>
                Plays
              </Chip>
            </div>
          </div>
        </div>

        {/* search */}
        <label className="relative block">
          <span className="sr-only">Search outcome or team</span>
          <input
            type="search"
            value={p.q}
            onChange={(e) => p.onQ(e.target.value)}
            placeholder="Search outcome or team"
            className="min-h-11 w-full rounded-full border border-border-2 bg-panel px-4 text-[14px] text-ink placeholder:text-faint focus:border-amber/60 focus:outline-none lg:min-h-9 lg:text-[13px]"
          />
        </label>

        {/* filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1" role="group" aria-label="Result">
            <Chip active={!p.result} onClick={() => p.onResult(null)}>
              All
            </Chip>
            {RESULT_FILTERS.map((r) => (
              <Chip key={r.id} active={p.result === r.id} onClick={() => p.onResult(p.result === r.id ? null : r.id)}>
                {r.label}
              </Chip>
            ))}
          </div>
          <SelectPill label="Venue" value={p.venue} options={p.venues} onChange={p.onVenue} />
          <SelectPill label="League" value={p.league} options={p.leagues} onChange={p.onLeague} />
          {p.day && (
            <button
              type="button"
              onClick={p.onClearDay}
              aria-label={`Clear day ${dayText(p.day.day)}`}
              className="flex min-h-11 items-center gap-1.5 rounded-full border border-amber/60 bg-amber/15 px-3 text-[13px] font-medium text-amber lg:min-h-8"
            >
              {dayText(p.day.day)} <span aria-hidden>✕</span>
            </button>
          )}
        </div>

        {p.day && <DayStats day={p.day} ccy={p.ccy} />}

        {p.clientFiltering && !p.loading && (
          <p className="text-[11px] text-faint">
            Filtering loaded bets ({p.loadedCount.toLocaleString()} loaded{p.hasMore ? "; load more to search further back" : ", all of them"}). The API
            can't filter by venue, league or text{plays ? ", and a play's result is its net" : ""}.
          </p>
        )}
        {plays && !p.loading && (
          <p className="text-[11px] text-faint">
            {playCount} play{playCount === 1 ? "" : "s"} grouped from loaded bets
            {p.hasMore ? "; a play near the end may have legs further back (load more)" : ""}.
          </p>
        )}
      </div>

      {/* column heads (desktop) */}
      <div className="hidden border-t border-border px-4 py-1.5 text-[10px] uppercase tracking-wide text-faint lg:grid lg:grid-cols-[4.5rem_minmax(0,1fr)_8.5rem_5.5rem_3.75rem_6rem] lg:gap-3">
        <span>Settled</span>
        <span>Outcome · game</span>
        <span>Venue</span>
        <span className="text-right">Stake</span>
        <span className="text-center">Result</span>
        <span className="text-right">P&L</span>
      </div>

      {p.loading ? (
        <ul className="divide-y divide-border border-t border-border">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-3 lg:px-4">
              <Skeleton className="h-4 w-12" />
              <div className="min-w-0 flex-1">
                <Skeleton className="h-4 w-48 max-w-full" />
                <Skeleton className="mt-1 h-3 w-32" />
              </div>
              <Skeleton className="h-4 w-16" />
            </li>
          ))}
        </ul>
      ) : p.isError ? (
        <div className="border-t border-border">
          <ErrorState error={p.error} onRetry={p.onRetry} />
        </div>
      ) : shown === 0 ? (
        <div className="border-t border-border px-4 py-6 text-center text-[13px] text-dim">
          <p>{p.emptyText}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {p.hasMore && (
              <button type="button" onClick={p.onLoadMore} disabled={p.loadingMore} className={btn}>
                {p.loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
            {anyFilter && (
              <button type="button" onClick={p.onClearAll} className={btn}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border border-t border-border">
            {plays
              ? p.items.map((it) => (it.type === "play" ? <PlayRow key={it.key} play={it.play} {...p} /> : <BetRow key={it.key} o={it.order} {...p} />))
              : p.rows.map((o) => <BetRow key={o.router_order_id} o={o} {...p} />)}
          </ul>
          {p.hasMore && (
            <div className="flex justify-center border-t border-border p-3">
              <button type="button" onClick={p.onLoadMore} disabled={p.loadingMore} className={btn}>
                {p.loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const btn =
  "min-h-11 rounded-full border border-border-2 bg-panel px-4 text-[13px] text-dim hover:bg-raised hover:text-ink disabled:opacity-50 lg:min-h-9";

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-h-11 items-center rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 motion-reduce:transition-none lg:min-h-8 ${
        active ? "border-amber/60 bg-amber/15 text-amber" : "border-border-2 bg-panel text-dim hover:bg-raised hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

/** A pill-shaped native select (long lists — a league list can run to 20+). Amber when set. */
function SelectPill({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: { key: string; label: string }[];
  onChange: (v: string | null) => void;
}) {
  const active = !!value;
  return (
    <label
      className={`relative flex min-h-11 max-w-full items-center rounded-full border text-[13px] font-medium lg:min-h-8 ${
        active ? "border-amber/60 bg-amber/15 text-amber" : "border-border-2 bg-panel text-dim hover:bg-raised"
      }`}
    >
      <span className="sr-only">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="min-h-11 max-w-[11rem] cursor-pointer appearance-none truncate rounded-full bg-transparent pl-3 pr-7 outline-none focus-visible:ring-2 focus-visible:ring-amber/60 lg:min-h-8"
      >
        <option value="">All {label.toLowerCase()}s</option>
        {value && !options.some((o) => o.key === value) && <option value={value}>{label}</option>}
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 text-[10px]" aria-hidden>
        ▾
      </span>
    </label>
  );
}

const dayText = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });

function DayStats({ day, ccy }: { day: DaySummary; ccy: Book }) {
  const g = day.group;
  const net = g?.net_pl ?? 0;
  const items: { k: string; v: ReactNode }[] = [
    { k: "Net", v: <span className={net >= 0.005 ? "text-up" : net <= -0.005 ? "text-down" : "text-dim"}>{fmtSignedAmt(net, ccy)}</span> },
    { k: "ROI", v: fmtRoi(g?.roi) },
    { k: "Record", v: `${g?.wins ?? 0}-${g?.losses ?? 0}-${g?.pushes ?? 0}` },
    { k: "Staked", v: fmtAmt(g?.staked ?? 0, ccy) },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 rounded-md border border-amber/30 bg-amber/5 p-2.5 sm:grid-cols-4" aria-label={`${dayText(day.day)} summary`}>
      {items.map((it) => (
        <div key={it.k} className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-faint">{it.k}</div>
          <div className="nums text-[14px] text-ink">{day.loading ? <Skeleton className="mt-0.5 h-4 w-14" /> : it.v}</div>
        </div>
      ))}
    </div>
  );
}

const RESULT_PILL: Record<string, { t: string; c: string }> = {
  win: { t: "Won", c: "border-up/40 bg-up/10 text-up" },
  lose: { t: "Lost", c: "border-down/40 bg-down/10 text-down" },
  push: { t: "Push", c: "border-border-2 bg-panel text-dim" },
};

function BetRow({ o, ccy, contestOf, leagueName, logos, useResolution }: { o: Order } & SettledListProps) {
  const [open, setOpen] = useState(false);
  const r = (o.result ?? "").toLowerCase();
  const pill = RESULT_PILL[r] ?? { t: r || "—", c: "border-border-2 text-dim" };
  const profit = betProfit(o);
  const c = o.contest_id ? contestOf(o.contest_id) : undefined;
  const game = gameLabel(c);
  const league = leagueName(o.contest_id);
  const venueName = o.partner?.partner_name ?? o.partner_id ?? "—";
  const logo = o.partner?.logo_url ?? (o.partner_id ? logos.get(o.partner_id) : undefined);
  const at = o.settled_datetime ? new Date(o.settled_datetime) : null;
  const date = at ? at.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
  const time = at ? at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  const pnl = (
    <span className={`nums font-medium ${profit >= 0.005 ? "text-up" : profit <= -0.005 ? "text-down" : "text-dim"}`}>{fmtSignedAmt(profit, ccy)}</span>
  );
  const resultPill = <span className={`inline-flex w-12 shrink-0 justify-center rounded-full border py-0.5 text-[11px] font-medium ${pill.c}`}>{pill.t}</span>;
  const gameLine = (
    <span className="min-w-0 truncate text-[12px] text-faint">
      {league && <span className="uppercase tracking-wide">{league} · </span>}
      {game ?? (o.contest_id && !c ? "…" : "—")}
    </span>
  );

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="block w-full px-3 py-2.5 text-left transition-colors duration-150 hover:bg-raised/50 motion-reduce:transition-none lg:px-4"
      >
        {/* mobile */}
        <div className="flex min-w-0 flex-col gap-1 lg:hidden">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-[14px] text-ink" title={o.title ?? undefined}>
              {shortOutcome(o.title)}
            </span>
            <span className="shrink-0 text-[14px]">{pnl}</span>
          </div>
          {gameLine}
          <div className="flex min-w-0 items-center gap-2 text-[12px] text-dim">
            {resultPill}
            <VenueLogo name={venueName} logoUrl={logo} size={14} />
            <span className="min-w-0 truncate">{venueName}</span>
            <span className="nums shrink-0">· {fmtAmt(o.amount ?? 0, ccy)}</span>
            <span className="nums ml-auto shrink-0 text-faint">{date}</span>
          </div>
        </div>
        {/* desktop */}
        <div className="hidden items-center gap-3 lg:grid lg:grid-cols-[4.5rem_minmax(0,1fr)_8.5rem_5.5rem_3.75rem_6rem]">
          <span className="nums text-[12px] leading-tight text-dim">
            {date}
            <span className="block text-[11px] text-faint">{time}</span>
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] text-ink" title={o.title ?? undefined}>
              {shortOutcome(o.title)}
            </span>
            {gameLine}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-dim">
            <VenueLogo name={venueName} logoUrl={logo} size={16} />
            <span className="truncate">{venueName}</span>
          </span>
          <span className="nums text-right text-[13px] text-dim">{fmtAmt(o.amount ?? 0, ccy)}</span>
          <span className="flex justify-center">{resultPill}</span>
          <span className="text-right text-[13px]">{pnl}</span>
        </div>
      </button>
      {open && (
        <div className="gb-fade border-t border-border/60 bg-bg/40 px-3 pb-3 lg:px-4">
          <ResolutionFor o={o} useResolution={useResolution} />
        </div>
      )}
    </li>
  );
}

/**
 * One settled play: date · kind chip + its outcomes · game · venues · total stake · result (by
 * the net) · net P&L. Tap for its legs (each a normal bet row, with its resolution).
 */
function PlayRow({ play, ...p }: { play: SettledPlay<Order> } & SettledListProps) {
  const [open, setOpen] = useState(false);
  const pill = RESULT_PILL[play.result];
  const c = play.contestId ? p.contestOf(play.contestId) : undefined;
  const game = gameLabel(c);
  const league = p.leagueName(play.contestId);
  const outcomes = [...new Set(play.legs.map((l) => outcomeLabel(l.title)))].join(" / ");
  const venues = [...new Map(play.legs.map((l) => [l.partner_id ?? l.partner?.partner_id ?? "", l])).values()];
  const venueText = venues.map((l) => l.partner?.partner_name ?? l.partner_id ?? "—").join(" · ");
  const at = new Date(play.settledAt);
  const date = play.settledAt ? at.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
  const time = play.settledAt ? at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  const pnl = (
    <span className={`nums font-medium ${play.net >= 0.005 ? "text-up" : play.net <= -0.005 ? "text-down" : "text-dim"}`}>{fmtSignedAmt(play.net, p.ccy)}</span>
  );
  const resultPill = <span className={`inline-flex w-12 shrink-0 justify-center rounded-full border py-0.5 text-[11px] font-medium ${pill.c}`}>{pill.t}</span>;
  const kindChip = (
    <span className="shrink-0 rounded-full border border-blue/40 bg-blue/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-blue">
      {PLAY_LABEL[play.kind]}
    </span>
  );
  const title = (
    <span className="flex min-w-0 items-center gap-1.5">
      {kindChip}
      <span className="min-w-0 truncate text-ink" title={play.legs.map((l) => l.title).join(" · ")}>
        {outcomes}
      </span>
      <span className="nums shrink-0 text-[11px] text-faint">
        {play.legs.length} legs {open ? "▾" : "▸"}
      </span>
    </span>
  );
  const gameLine = (
    <span className="min-w-0 truncate text-[12px] text-faint">
      {league && <span className="uppercase tracking-wide">{league} · </span>}
      {game ?? (play.contestId && !c ? "…" : "—")}
    </span>
  );
  const logos = (
    <span className="flex shrink-0 -space-x-1">
      {venues.slice(0, 3).map((l, i) => (
        <span key={i} className="rounded-full ring-2 ring-panel-2">
          <VenueLogo name={l.partner?.partner_name ?? l.partner_id ?? "—"} logoUrl={l.partner?.logo_url ?? (l.partner_id ? p.logos.get(l.partner_id) : undefined)} size={14} />
        </span>
      ))}
    </span>
  );
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="block w-full px-3 py-2.5 text-left transition-colors duration-150 hover:bg-raised/50 motion-reduce:transition-none lg:px-4"
      >
        {/* mobile */}
        <div className="flex min-w-0 flex-col gap-1 lg:hidden">
          <div className="flex min-w-0 items-baseline gap-2 text-[14px]">
            <span className="min-w-0 flex-1">{title}</span>
            <span className="shrink-0">{pnl}</span>
          </div>
          {gameLine}
          <div className="flex min-w-0 items-center gap-2 text-[12px] text-dim">
            {resultPill}
            {logos}
            <span className="min-w-0 truncate">{venueText}</span>
            <span className="nums shrink-0">· {fmtAmt(play.stake, p.ccy)}</span>
            <span className="nums ml-auto shrink-0 text-faint">{date}</span>
          </div>
        </div>
        {/* desktop */}
        <div className="hidden items-center gap-3 lg:grid lg:grid-cols-[4.5rem_minmax(0,1fr)_8.5rem_5.5rem_3.75rem_6rem]">
          <span className="nums text-[12px] leading-tight text-dim">
            {date}
            <span className="block text-[11px] text-faint">{time}</span>
          </span>
          <span className="flex min-w-0 flex-col text-[13px]">
            {title}
            {gameLine}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-dim">
            {logos}
            <span className="truncate">{venueText}</span>
          </span>
          <span className="nums text-right text-[13px] text-dim">{fmtAmt(play.stake, p.ccy)}</span>
          <span className="flex justify-center">{resultPill}</span>
          <span className="text-right text-[13px]">{pnl}</span>
        </div>
      </button>
      {open && (
        <ul className="gb-fade divide-y divide-border/60 border-t border-border/60 bg-bg/40 pl-3 lg:pl-6">
          {play.legs.map((o) => (
            <BetRow key={o.router_order_id} o={o} {...p} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ResolutionFor({ o, useResolution }: { o: Order; useResolution: SettledListProps["useResolution"] }) {
  const q = useResolution(o.router_order_id, true);
  return <ResolutionDetail order={o} state={q} />;
}

/** The empty state for a whole range (docs/history-ux.md §2). */
export function EmptyHistory({ text, onShowAll }: { text: string; onShowAll?: () => void }) {
  return (
    <div className="rounded-lg border border-border bg-panel-2 px-4 py-10 text-center">
      <p className="text-[14px] text-ink">{text}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Link to="/markets" className="inline-flex min-h-11 items-center rounded-full border border-amber/60 bg-amber/15 px-4 text-[13px] font-medium text-amber hover:bg-amber/25 lg:min-h-9">
          Browse markets
        </Link>
        {onShowAll && (
          <button type="button" onClick={onShowAll} className={btn}>
            Show all time
          </button>
        )}
      </div>
    </div>
  );
}
